import React, { FC } from 'react';
import { 
	ActionStatusHelper, 
	Alert, 
	DOMAIN_CONFIGS, 
	DOMAIN_TYPES, 
	FeedBackType, 
	MessageHelper, 
	ModalHook, 
	PageLoader, 
	UIButton, 
	useAppNavigation, 
	useBreakPoint, 
	useUIActions
} from '@/Shared';

import { FaFileAlt } from 'react-icons/fa';
import { useDocumentCategory } from '../../Logic';
import GenericDomainForm from '@/Shared/UI/Forms/GenericDomainForm';

interface DocumentCategorySelectorProps {
	inModalHook?:ModalHook
}

/**
 * Component: DocumentCategorySelector
 *
 * Description:
 * This component provides a visual navigation layer for document creation.
 * It displays all available document categories and their associated types,
 * allowing the user to quickly start a new document from a predefined context.
 *
 * It is NOT a form component.
 * Its sole responsibility is to expose category/type entry points
 * and redirect the user to the appropriate document creation route.
 *
 * Responsibilities:
 * - Render document categories and their types
 * - Highlight the currently selected document type
 * - Generate navigation links for document creation
 *
 * Design principles:
 * - Stateless and declarative
 * - Navigation-oriented (no form logic)
 * - Domain-driven UI (categories and types come from configuration)
 *
 * Author: Samano CASTRE
 */
const DocumentCategorySelector: FC<DocumentCategorySelectorProps> = ({inModalHook}) => {
	
	const {goToForm} = useAppNavigation();
	const { docCategoryList, accessRight, categoryResponse : response } = useDocumentCategory();

	const {domainType, domainLabel} = DOMAIN_CONFIGS.Document;
	const { isDesktop } = useBreakPoint();

	const {computeGlobalUIActions} = useUIActions({
		//domains: [DOMAIN_TYPES.Lease, DOMAIN_TYPES.Property, DOMAIN_TYPES.Mandate],
		accessRight,
		domainType,
		inModal : !!inModalHook,
		modalHook : inModalHook
	});

	 /** Check for fetch or form response errors */
    const { isLoading, isFailed, isIdle } = ActionStatusHelper.is(response.status);

    // Handle fetch or form response errors
    if (isFailed) {
        return (
            <Alert
                data={{
                    message: response.error || MessageHelper.FETCHING.ERROR(domainLabel),
                    type: FeedBackType.ERROR
                }}
            />
        );
    }

    /** Display loader while domain form data is being fetched */
    if (isLoading || isIdle) {
        return (
            <PageLoader
                message={MessageHelper.FETCHING.LOADING(domainLabel)}
            />
        );
    }

	const goToType = (category : string, type : string) => {
		if(inModalHook) {
			const content = <GenericDomainForm
                domainType={DOMAIN_TYPES.Document}
                inModalHook={inModalHook}
                extraArgs={{
                    curType : type
                }}
            />
			return inModalHook.openModal({
				title: `Document : (${category} - ${type})`,
				content
			});
		}
        return goToForm(DOMAIN_TYPES.Document, undefined, category+'/'+type);
    }


	/**
	 * Renders a categorized list of document types.
	 *
	 * Each category is displayed as a fieldset containing
	 * clickable entries for document creation.
	 *
	 * The currently active document type is visually highlighted
	 * to reflect the current navigation context.
	 */


	return (
		<div className="DocumentCategorySelector form fade-in" data-testid="DocumentCategorySelector">
			<div className="form-header">
				<h3>Choix du type de documents</h3>
			</div>
			<div className="details-page__body sm-scroll">

				<div className="sub-actions flex-row">
					{ computeGlobalUIActions().map(action =>
							<UIButton
								key={action.key}
								action={action}
								isDesktop={isDesktop}
							/>
					)}
				</div>

				<div className="fieldset-wrapper">
					{
						docCategoryList.map(cat => (
							<fieldset key={cat.key}>
								<legend className="with-icon">
									<FaFileAlt /> <h3>{cat.label}</h3>
								</legend>
								<ul className="DocumentCategorySelector__list">
									{
										cat.children?.map(
											child => (<li
													key={child.key}
													className='DocumentCategorySelector__item'
													onClick={() =>goToType(cat.label, child.label)}
												>
												<span className="link-bullet">+</span>
												<span className="link-value">{child.label}</span>
											</li>
											)
										)
									}
								</ul>
							</fieldset>
						))
					}
				</div>
			</div>
		</div>
	);
};

export default DocumentCategorySelector;

/**
 * <Link to={`/documents/selector/${cat.label + '/' + type.label}`}>
													<span className="link-bullet">+</span>
													<span className="link-value">{type.label}</span>
												</Link>
 */
